'use client';

/**
 * The Beam identity that used to ride in the meeting URL.
 *
 * Beam Live links people here as
 *   https://page.beam.live/?pageId=251769&u=106501&o=cudb-live&metadata=106501
 * where `u` / `o` identify the Beam user and org, and `metadata` is the suffix
 * that keeps their participant identity stable across rejoins.
 *
 * Those three are personal, and a URL is the one thing people copy and paste to
 * each other. Left in place, "share the link" means "share my account handle and
 * join as me". So on first load we lift them into localStorage and rewrite the
 * address bar down to `?pageId=…`, which is then safe to hand to anyone — and,
 * being identity-free, drops the recipient into the waiting room instead of
 * straight into the call.
 *
 * localStorage rather than sessionStorage: this is the user's Beam identity, and
 * it should still be theirs in a new tab tomorrow, the same way the rest of the
 * app treats a login.
 */

const STORAGE_KEYS = {
  userId: 'beam-meet-user-id',
  partitionId: 'beam-meet-partition-id',
  metadata: 'beam-meet-metadata',
} as const;

/** Query params lifted out of the URL and kept in storage instead. */
const URL_KEYS = ['u', 'o', 'metadata'] as const;

export interface SessionParams {
  userId?: string;
  partitionId?: string;
  metadata?: string;
}

const readStored = (key: string): string | undefined => {
  try {
    return window.localStorage.getItem(key) || undefined;
  } catch {
    // Private browsing, blocked site data — treat as "nothing stored". Falling
    // through to the URL values keeps the current visit working.
    return undefined;
  }
};

const writeStored = (key: string, value?: string): void => {
  try {
    if (value) window.localStorage.setItem(key, value);
  } catch {}
};

/**
 * Resolve the Beam identity for this visit and clean the address bar.
 *
 * URL values win over stored ones (arriving with `u=` means "I am this person
 * now") and are persisted as they are consumed. Anything absent from the URL
 * falls back to what was stored on a previous visit, which is what lets a
 * returning Beam user open a bare `?pageId=` link and still be recognised.
 *
 * Call once, from a client effect. Safe to call twice — the second call finds
 * the params already gone from the URL and reads them back out of storage.
 */
export const resolveSessionParams = (fromUrl: SessionParams = {}): SessionParams => {
  if (typeof window === 'undefined') return {};

  writeStored(STORAGE_KEYS.userId, fromUrl.userId);
  writeStored(STORAGE_KEYS.partitionId, fromUrl.partitionId);
  writeStored(STORAGE_KEYS.metadata, fromUrl.metadata);

  stripIdentityFromUrl();

  return {
    userId: fromUrl.userId || readStored(STORAGE_KEYS.userId),
    partitionId: fromUrl.partitionId || readStored(STORAGE_KEYS.partitionId),
    metadata: fromUrl.metadata || readStored(STORAGE_KEYS.metadata),
  };
};

/**
 * Drop `u` / `o` / `metadata` from the address bar, leaving `pageId` and every
 * other param (`viewer`, `region`, `hq`, `codec`, the e2ee hash) untouched.
 *
 * `history.replaceState` rather than a router call on purpose: Next would treat
 * a navigation as a new render of the page with different searchParams, which
 * would remount the room mid-join. This only edits what the address bar shows.
 */
const stripIdentityFromUrl = (): void => {
  try {
    const url = new URL(window.location.href);
    const present = URL_KEYS.filter((key) => url.searchParams.has(key));
    if (present.length === 0) return;
    present.forEach((key) => url.searchParams.delete(key));
    window.history.replaceState(window.history.state, '', url.toString());
  } catch {}
};

/**
 * The link to hand to someone else: this meeting, nobody's identity attached.
 *
 * Built from the current origin so it stays correct across the staging and
 * production deployments without another environment variable to keep in sync.
 */
export const buildShareUrl = (roomName: string): string => {
  if (typeof window === 'undefined') return '';
  return `${window.location.origin}/?pageId=${encodeURIComponent(roomName)}`;
};

/**
 * Copy text, falling back to the legacy `execCommand` path.
 *
 * `navigator.clipboard` is undefined on a page served over plain HTTP, which is
 * exactly how this app is reached on a LAN deployment — so the modern API alone
 * would leave the button silently dead for the people most likely to need it.
 */
export const copyToClipboard = async (text: string): Promise<boolean> => {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {}
  try {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(textarea);
    return ok;
  } catch {
    return false;
  }
};
